/**
 * Cognito Hosted UI OAuth code flow with PKCE.
 *
 * Flow:
 *  1. login() — generates PKCE verifier, redirects to Cognito hosted UI
 *  2. Cognito redirects back to /callback?code=... after user signs in
 *  3. exchangeCode() — POST to /oauth2/token to swap code for tokens
 *  4. Tokens persisted in localStorage; id_token used as Bearer for API calls
 *  5. logout() — clears local state and redirects to Cognito logout
 */

const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN;
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;
const REDIRECT_URI = import.meta.env.VITE_COGNITO_REDIRECT_URI;
const SCOPES = "openid email profile";

const STORAGE_KEYS = {
  idToken: "ai_coach_id_token",
  accessToken: "ai_coach_access_token",
  refreshToken: "ai_coach_refresh_token",
  expiresAt: "ai_coach_expires_at",
  pkceVerifier: "ai_coach_pkce_verifier",
};

// ─── PKCE helpers ─────────────────────────────────────────────────────────────

function base64UrlEncode(buffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

function generateRandomString(length = 64) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

async function sha256(input) {
  const encoded = new TextEncoder().encode(input);
  return crypto.subtle.digest("SHA-256", encoded);
}

// ─── Public API ───────────────────────────────────────────────────────────────

async function startHostedUiFlow(endpoint) {
  const verifier = generateRandomString();
  const challenge = base64UrlEncode(await sha256(verifier));
  sessionStorage.setItem(STORAGE_KEYS.pkceVerifier, verifier);

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: "code",
    scope: SCOPES,
    redirect_uri: REDIRECT_URI,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });

  window.location.assign(`${COGNITO_DOMAIN}${endpoint}?${params}`);
}

export async function login() {
  await startHostedUiFlow("/oauth2/authorize");
}

/**
 * Route directly to the hosted UI's /signup page. The default
 * /oauth2/authorize shows a sign-in form with a small "Sign up" link that
 * users were missing — this entry point opens the sign-up form directly.
 * After a successful confirmation the flow returns via /callback just like
 * login(), so no extra wiring is needed downstream.
 */
export async function signup() {
  await startHostedUiFlow("/signup");
}

export async function exchangeCode(code) {
  const verifier = sessionStorage.getItem(STORAGE_KEYS.pkceVerifier);
  if (!verifier) throw new Error("Missing PKCE verifier — please retry login");

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: CLIENT_ID,
    code,
    redirect_uri: REDIRECT_URI,
    code_verifier: verifier,
  });

  const response = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Token exchange failed: ${text}`);
  }

  const tokens = await response.json();
  persistTokens(tokens);
  sessionStorage.removeItem(STORAGE_KEYS.pkceVerifier);
  return tokens;
}

function persistTokens(tokens) {
  localStorage.setItem(STORAGE_KEYS.idToken, tokens.id_token);
  localStorage.setItem(STORAGE_KEYS.accessToken, tokens.access_token);
  if (tokens.refresh_token) {
    localStorage.setItem(STORAGE_KEYS.refreshToken, tokens.refresh_token);
  }
  const expiresAt = Date.now() + tokens.expires_in * 1000;
  localStorage.setItem(STORAGE_KEYS.expiresAt, String(expiresAt));
}

export function getIdToken() {
  return localStorage.getItem(STORAGE_KEYS.idToken);
}

export function isAuthenticated() {
  const token = getIdToken();
  const expiresAt = Number(localStorage.getItem(STORAGE_KEYS.expiresAt) || 0);
  return Boolean(token) && Date.now() < expiresAt;
}

export function logout() {
  Object.values(STORAGE_KEYS).forEach((k) => {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  });
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    logout_uri: REDIRECT_URI.replace("/callback", ""),
  });
  window.location.assign(`${COGNITO_DOMAIN}/logout?${params}`);
}

export function getUserInfo() {
  const token = getIdToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return { sub: payload.sub, email: payload.email };
  } catch {
    return null;
  }
}

/**
 * Extract Cognito group membership from the ID token.
 *
 * Cognito encodes `cognito:groups` as a JSON array in the ID token
 * payload when the user has any groups; if the user is in no groups
 * the claim is absent. We return an empty array for ungrouped users.
 */
export function getUserGroups() {
  const token = getIdToken();
  if (!token) return [];
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const groups = payload["cognito:groups"];
    if (!groups) return [];
    return Array.isArray(groups) ? groups : [groups];
  } catch {
    return [];
  }
}

/** Admin group membership — gates the Phase 27 admin panel. */
export function isAdmin() {
  return getUserGroups().includes("admin");
}
